import React, { useState } from 'react';
import { Modal, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs } from '../ui/tabs';
import { useDivisionUser, useUserActivity } from '../../hooks/use-division-users';
import { useActivateDivisionUser } from '../../hooks/use-division-users';
import { useUserWorkflowAssociations } from '../../hooks/use-division-workflows';
import { UserFormModal } from './user-form-modal';
import { AddUserToDivisionModal } from './add-user-to-division-modal';
import { UserMatchIdentifiersTab } from './UserMatchIdentifiersTab';
import { Shield, ShieldCheck, Mail, MapPin, Calendar, Activity, Building2, GitBranch } from 'lucide-react';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  userId: string;
  readOnly?: boolean;
}

export function UserDetailModal({ 
  isOpen, 
  onClose, 
  divisionId,
  userId,
  readOnly = false,
}: UserDetailModalProps) {
  const [activeTab, setActiveTab] = useState('details');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddToDivisionModalOpen, setIsAddToDivisionModalOpen] = useState(false);

  const { data: user, isLoading: isLoadingUser } = useDivisionUser(divisionId, userId, isOpen);
  const { data: activity } = useUserActivity(divisionId, userId, undefined, isOpen);
  const { data: workflowAssociations, isLoading: isLoadingWorkflows } = useUserWorkflowAssociations(divisionId, userId, isOpen);
  const activateUserMutation = useActivateDivisionUser(divisionId);

  const handleActivateUser = async () => {
    try {
      await activateUserMutation.mutateAsync(userId);
    } catch (error) {
      console.error('Failed to activate user:', error);
    }
  };

  if (!isOpen) return null;

  if (isLoadingUser) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="User Details">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading user details...</span>
        </div>
      </Modal>
    );
  }

  if (!user) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="User Details">
        <div className="text-center py-8">
          <div className="text-red-600 dark:text-red-400 mb-2">User not found</div>
          <div className="text-sm text-secondary-500 dark:text-secondary-400">
            The requested user could not be loaded.
          </div>
        </div>
      </Modal>
    );
  }

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? 'success' : 'error'}>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      'Admin': { variant: 'error' as const, icon: ShieldCheck },
      'DivisionAdmin': { variant: 'warning' as const, icon: Shield },
      'Approver': { variant: 'info' as const, icon: Shield },
      'Shopper': { variant: 'default' as const, icon: null },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.Shopper;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <div className="flex items-center space-x-1">
          {Icon && <Icon className="h-3 w-3" />}
          <span>{role}</span>
        </div>
      </Badge>
    );
  };

  const tabs = [
    {
      id: 'details',
      label: 'User Details',
      content: (
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Full Name</label>
                <div className="mt-1 text-secondary-900 dark:text-white">{user.firstName} {user.lastName}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Email</label>
                <div className="mt-1 flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-secondary-400" />
                  <span className="text-secondary-900 dark:text-white">{user.email}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Status</label>
                <div className="mt-1">{getStatusBadge(user.active)}</div>
                {!user.active && user.deactivatedAt && (
                  <div className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                    {user.deactivationReason || 'Manual'} on {new Date(user.deactivatedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">External ID</label>
                <div className="mt-1 text-secondary-900 dark:text-white">{user.externalUserId || 'Not set'}</div>
              </div>
            </div>
          </div>

          {/* Address Information */}
          {(user.address || user.city || user.state || user.zipCode) && (
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Address</h3>
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-secondary-400 mt-1" />
                <div className="text-secondary-900 dark:text-white">
                  {user.address && <div>{user.address}</div>}
                  {(user.city || user.state || user.zipCode) && (
                    <div>
                      {[user.city, user.state, user.zipCode].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Roles */}
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4 flex items-center justify-between">
              <span>Roles & Permissions</span>
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddToDivisionModalOpen(true)}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Add to Division
                </Button>
              )}
            </h3>
            <div className="space-y-3">
              {user.divisionRoles && user.divisionRoles.length > 0 ? (
                user.divisionRoles.map((divRole, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                    <div>
                      <div className="font-medium text-secondary-900 dark:text-white">{divRole.divisionName || 'Current Division'}</div>
                      <div className="text-sm text-secondary-500 dark:text-secondary-400">Role: {divRole.role}</div>
                    </div>
                    <div>{getRoleBadge(divRole.role)}</div>
                  </div>
                ))
              ) : (
                <div className="text-secondary-500 dark:text-secondary-400 italic">No roles assigned</div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Created</label>
                <div className="mt-1 flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-secondary-400" />
                  <span className="text-secondary-900 dark:text-white">{new Date(user.createdAt).toLocaleString()}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Last Updated</label>
                <div className="mt-1 flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-secondary-400" />
                  <span className="text-secondary-900 dark:text-white">{new Date(user.updatedAt).toLocaleString()}</span>
                </div>
              </div>
              {user.deactivatedAt && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">Deactivated</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-secondary-400" />
                    <span className="text-secondary-900 dark:text-white">
                      {new Date(user.deactivatedAt).toLocaleString()} ({user.deactivationReason || 'Manual'})
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'activity',
      label: 'Activity',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white">User Activity</h3>
          
          {activity ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div className="text-sm font-medium text-blue-800">Total Orders</div>
                </div>
                <div className="text-2xl font-bold text-blue-900 mt-1">{activity.totalOrders}</div>
              </div>
              
              <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-yellow-600" />
                  <div className="text-sm font-medium text-yellow-800">Pending Approvals</div>
                </div>
                <div className="text-2xl font-bold text-yellow-900 mt-1">{activity.pendingApprovals}</div>
              </div>
              
              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div className="text-sm font-medium text-green-800">Completed Approvals</div>
                </div>
                <div className="text-2xl font-bold text-green-900 mt-1">{activity.completedApprovals}</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-secondary-500 dark:text-secondary-400">Activity data not available</div>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'workflows',
      label: 'Workflows',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white">Associated Workflows</h3>

          {isLoadingWorkflows ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading workflows...</span>
            </div>
          ) : workflowAssociations && workflowAssociations.length > 0 ? (
            <div className="space-y-3">
              {workflowAssociations.map((workflow) => (
                <div key={workflow.workflowId} className="p-4 bg-secondary-50 dark:bg-secondary-900 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <GitBranch className="h-4 w-4 text-secondary-400" />
                      <span className="font-medium text-secondary-900 dark:text-white">{workflow.workflowName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="default">{workflow.workflowType}</Badge>
                      <Badge variant={workflow.active ? 'success' : 'error'}>
                        {workflow.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {workflow.steps.map((step) => (
                      <div
                        key={step.step}
                        className="flex items-center justify-between text-sm text-secondary-600 dark:text-secondary-300 pl-6"
                      >
                        <span>Step {step.step}: {step.name}</span>
                        <span className="text-secondary-400 dark:text-secondary-500 text-xs">{step.matchedBy}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-secondary-500 dark:text-secondary-400 italic">
              This user is not configured as an approver on any workflow in this division.
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`${user.firstName} ${user.lastName}`} size="xl">
        <Tabs
          tabs={tabs}
          activeTabId={activeTab}
          onTabChange={setActiveTab}
        />

        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {!readOnly && !user.active && (
            <Button
              onClick={handleActivateUser}
              disabled={activateUserMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Reactivate User
            </Button>
          )}
          {!readOnly && (
            <Button onClick={() => setIsEditModalOpen(true)}>
              Edit User
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Edit User Modal */}
      {isEditModalOpen && (
        <UserFormModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          divisionId={divisionId}
          mode="edit"
          userId={userId}
        />
      )}

      {/* Add User to Division Modal */}
      {isAddToDivisionModalOpen && (
        <AddUserToDivisionModal
          isOpen={isAddToDivisionModalOpen}
          onClose={() => setIsAddToDivisionModalOpen(false)}
          userEmail={user.email}
          userName={`${user.firstName} ${user.lastName}`}
          currentDivisionIds={user.divisionRoles?.map(dr => dr.divisionId) || []}
          currentDivisions={user.divisionRoles || []}
        />
      )}
    </>
  );
}
